import {
  type DirectionResult,
  estimateDirection,
  type LocalizationConfig,
} from "../localization/direction"
import { BinauralEngine } from "./binaural"
import type { BinauralConfig, BinauralPlan } from "./binauralPlan"
import { AudioRouter } from "./router"

export const INPUT_SLOTS = ["a", "b", "c"] as const
export type InputSlot = (typeof INPUT_SLOTS)[number]

type InputGraph = {
  readonly stream: MediaStream
  readonly source: MediaStreamAudioSourceNode
  readonly analyser: AnalyserNode
  readonly channelAnalysers: readonly AnalyserNode[]
  readonly channelCount: number
}

export type AnalysisSnapshot = {
  readonly frequency: Uint8Array
  readonly waveform: Float32Array
  readonly sampleRate: number
  readonly channelCount: number
}

export class InputCaptureError extends Error {
  readonly causeName: string

  constructor(message: string, causeName: string) {
    super(message)
    this.name = "InputCaptureError"
    this.causeName = causeName
  }
}

export class ApplicationAudio {
  readonly context: AudioContext
  readonly router: AudioRouter
  readonly binaural: BinauralEngine
  private readonly inputs = new Map<InputSlot, InputGraph>()
  private readonly routedElement: HTMLAudioElement

  constructor() {
    this.routedElement = document.createElement("audio")
    this.routedElement.hidden = true
    document.body.append(this.routedElement)
    this.context = new AudioContext({ latencyHint: "interactive" })
    this.router = new AudioRouter(this.context, this.routedElement)
    this.binaural = new BinauralEngine(this.context, this.router.input)
  }

  get running(): boolean {
    return this.inputs.size > 0
  }

  async listDevices(): Promise<readonly MediaDeviceInfo[]> {
    return navigator.mediaDevices.enumerateDevices()
  }

  async startInput(slot: InputSlot, deviceId: string): Promise<number> {
    this.stopInput(slot)
    let stream: MediaStream | undefined
    try {
      const sharedConstraints = {
        channelCount: slot === "a" ? { ideal: 3 } : { ideal: 1 },
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      } satisfies MediaTrackConstraints
      const audioConstraints: MediaTrackConstraints =
        deviceId === "default"
          ? sharedConstraints
          : { ...sharedConstraints, deviceId: { exact: deviceId } }
      stream = await navigator.mediaDevices.getUserMedia({
        audio: audioConstraints,
        video: false,
      })
      const source = this.context.createMediaStreamSource(stream)
      const analyser = this.context.createAnalyser()
      analyser.fftSize = 4_096
      analyser.smoothingTimeConstant = 0.72
      source.connect(analyser)
      source.connect(this.router.monitorInput)
      const settings = stream.getAudioTracks()[0]?.getSettings()
      const channelCount = settings?.channelCount ?? 1
      const splitter = this.context.createChannelSplitter(Math.max(1, channelCount))
      source.connect(splitter)
      const channelAnalysers: AnalyserNode[] = []
      for (let channel = 0; channel < channelCount; channel += 1) {
        const channelAnalyser = this.context.createAnalyser()
        channelAnalyser.fftSize = 4_096
        splitter.connect(channelAnalyser, channel, 0)
        channelAnalysers.push(channelAnalyser)
      }
      this.inputs.set(slot, { stream, source, analyser, channelAnalysers, channelCount })
      return channelCount
    } catch (cause) {
      stream?.getTracks().forEach((track) => {
        track.stop()
      })
      if (cause instanceof DOMException) {
        throw new InputCaptureError(cause.message, cause.name)
      }
      throw cause
    }
  }

  stopInput(slot: InputSlot): void {
    const graph = this.inputs.get(slot)
    if (graph === undefined) return
    graph.source.disconnect()
    graph.stream.getTracks().forEach((track) => {
      track.stop()
    })
    this.inputs.delete(slot)
  }

  stopAllInputs(): void {
    for (const slot of INPUT_SLOTS) this.stopInput(slot)
  }

  snapshot(): AnalysisSnapshot | undefined {
    const graph = this.inputs.get("a")
    if (graph === undefined) return undefined
    const frequency = new Uint8Array(graph.analyser.frequencyBinCount)
    const waveform = new Float32Array(graph.analyser.fftSize)
    graph.analyser.getByteFrequencyData(frequency)
    graph.analyser.getFloatTimeDomainData(waveform)
    return {
      frequency,
      waveform,
      sampleRate: this.context.sampleRate,
      channelCount: graph.channelCount,
    }
  }

  direction(config: LocalizationConfig): DirectionResult {
    const graph = this.inputs.get("a")
    if (graph === undefined || graph.channelAnalysers.length < config.microphoneCount) {
      return { kind: "insufficient_signal", reason: "low_energy" }
    }
    const channels = graph.channelAnalysers.slice(0, config.microphoneCount).map((analyser) => {
      const samples = new Float32Array(analyser.fftSize)
      analyser.getFloatTimeDomainData(samples)
      return samples
    })
    return estimateDirection(channels, config)
  }

  startBinaural(config: BinauralConfig): BinauralPlan {
    return this.binaural.start(config)
  }

  updateBinaural(config: BinauralConfig): BinauralPlan {
    return this.binaural.update(config)
  }

  stopBinaural(): void {
    this.binaural.stop()
  }

  async resume(): Promise<void> {
    await this.context.resume()
  }

  close(): void {
    this.stopBinaural()
    this.stopAllInputs()
    this.routedElement.remove()
    void this.context.close()
  }
}
